var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};

// src/JsonViewer.ts
import { html, LitElement, nothing } from "lit";
import { property, queryAll, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { map } from "lit/directives/map.js";
import { when } from "lit/directives/when.js";

// src/utils.ts
function isRegex(value) {
  return value instanceof RegExp;
}
function getType(value) {
  return value === null ? "null" /* Null */ : Array.isArray(value) ? "array" /* Array */ : value.constructor.name.toLowerCase();
}
function isPrimitive(value) {
  return value !== Object(value);
}
function generateNodePreview(node, { nodeCount = 3, maxLength = 15 } = {}) {
  const isArray = Array.isArray(node);
  const objectNodes = Object.keys(node);
  const keys = objectNodes.slice(0, nodeCount);
  const preview = [];
  const getNodePreview = (nodeValue) => {
    const nodeType = getType(nodeValue);
    switch (nodeType) {
      case "object" /* Object */:
        return Object.keys(nodeValue).length === 0 ? "{ }" : "{ ... }";
      case "array" /* Array */:
        return nodeValue.length === 0 ? "[ ]" : "[ ... ]";
      case "string" /* String */:
        return `"${nodeValue.substring(0, maxLength)}${nodeValue.length > maxLength ? "..." : ""}"`;
      default:
        return String(nodeValue);
    }
  };
  const childPreviews = [];
  for (const key of keys) {
    const nodePreview = [];
    const nodeValue = node[key];
    if (!isArray) nodePreview.push(`${key}: `);
    nodePreview.push(getNodePreview(nodeValue));
    childPreviews.push(nodePreview.join(""));
  }
  if (objectNodes.length > nodeCount) {
    childPreviews.push("...");
  }
  preview.push(childPreviews.join(", "));
  const previewText = preview.join("");
  return isArray ? `[ ${previewText} ]` : `{ ${previewText} }`;
}
function* deepTraverse(obj) {
  const stack = [[obj, "", []]];
  while (stack.length) {
    const [node, path, parents] = stack.shift();
    if (path) {
      yield [node, path, parents];
    }
    if (!isPrimitive(node)) {
      for (const [key, value] of Object.entries(node)) {
        stack.push([value, `${path}${path ? "." : ""}${key}`, [...parents, path]]);
      }
    }
  }
}
function checkGlob(str, glob) {
  const strParts = str.split(".");
  const globaParts = glob.split(".");
  const isStar = (s) => s === "*";
  const isGlobStar = (s) => s === "**";
  let strIndex = 0;
  let globIndex = 0;
  while (strIndex < strParts.length) {
    const globPart = globaParts[globIndex];
    const strPart = strParts[strIndex];
    if (globPart === strPart || isStar(globPart)) {
      globIndex++;
      strIndex++;
    } else if (isGlobStar(globPart)) {
      globIndex++;
      strIndex = strParts.length - (globaParts.length - globIndex);
    } else {
      return false;
    }
  }
  return globIndex === globaParts.length;
}
var JSONConverter = {
  fromAttribute: (value) => {
    return value && value.trim() ? JSON.parse(value) : void 0;
  },
  toAttribute: (value) => {
    return JSON.stringify(value);
  }
};
var isDefined = (value) => value !== void 0;
var isMatchingPath = (path, criteria) => isRegex(criteria) ? !!path.match(criteria) : checkGlob(path, criteria);
var getValueByPath = (json, path) => path.split(".").reduce((acc, key) => acc[key], json);

// src/stateChange.ts
var toggleNode = (path, expanded) => (state2) => ({
  expanded: {
    ...state2.expanded,
    [path]: isDefined(expanded) ? !!expanded : !state2.expanded[path]
  }
});
var expand = (regexOrGlob, isExpanded) => (_state, el) => {
  const expanded = {};
  if (regexOrGlob) {
    for (const [, path, parents] of deepTraverse(el.data)) {
      if (isMatchingPath(path, regexOrGlob)) {
        expanded[path] = isExpanded;
        parents.forEach((p) => expanded[p] = isExpanded);
      }
    }
  }
  return { expanded };
};
var filter = (regexOrGlob) => (_state, el) => {
  const filtered = {};
  if (regexOrGlob) {
    for (const [, path, parents] of deepTraverse(el.data)) {
      if (isMatchingPath(path, regexOrGlob)) {
        filtered[path] = false;
        parents.forEach((p) => filtered[p] = false);
      } else {
        filtered[path] = true;
      }
    }
  }
  return { filtered };
};
var resetFilter = () => () => ({ filtered: {} });
var highlight = (path) => () => ({
  highlight: path
});

// src/JsonViewer.styles.ts
import { css } from "lit";
var JsonViewer_styles_default = css`
    :where(:host) {
        --background-color: #2a2f3a;
        --color: #f8f8f2;
        --string-color: #a3eea0;
        --number-color: #d19a66;
        --boolean-color: #4ba7ef;
        --null-color: #df9cf3;
        --property-color: #6fb3d2;
        --preview-color: rgba(222, 175, 143, 0.9);
        --highlight-color:  #c92a2a;
        --outline-color: #e0e4e5;
        --outline-width: 1px;
        --outline-style: dotted;

        --font-family: Nimbus Mono PS, Courier New, monospace;
        --font-size: 1rem;
        --line-height: 1.2rem;

        --indent-size: 0.5rem;
        --indentguide-size: 1px;
        --indentguide-style: solid;
        --indentguide-color: #495057;
        --indentguide-color-active: #ced4da;
        --indentguide: var(--indentguide-size) var(--indentguide-style) var(--indentguide-color);
        --indentguide-active: var(--indentguide-size) var(--indentguide-style) var(--indentguide-color-active);
    }

    :host {
        display: block;
        background-color: var(--background-color);
        color: var(--color);
        font-family: var(--font-family);
        font-size: var(--font-size);
        line-height: var(--line-height);
    }

    :focus {
        outline-color: var(--outline-color);
        outline-width: var(--outline-width);
        outline-style: var(--outline-style);
    }

    .preview {
        color: var(--preview-color);
    }

    .null {
        color: var(--null-color);
    }

    .key {
        color: var(--property-color);
        display: inline-flex;
        align-items: flex-start;
    }

    .collapsable::before {
        display: inline-flex;
        font-size: 0.8em;
        content: '▶';
        width: var(--line-height);
        height: var(--line-height);
        align-items: center;
        justify-content: center;

        transition: transform 195ms ease-out;
        transform: rotate(90deg);

        color: inherit;
    }

    .collapsable--collapsed::before {
        transform: rotate(0);
    }

    .collapsable {
        cursor: pointer;
        user-select: none;
    }

    .string {
        color: var(--string-color);
    }

    .number {
        color: var(--number-color);
    }

    .boolean {
        color: var(--boolean-color);
    }

    ul {
        padding: 0;
        clear: both;
    }

    ul,
    li {
        list-style: none;
        position: relative;
    }

    li ul > li {
        position: relative;
        margin-left: calc(var(--indent-size) + var(--line-height));
        padding-left: 0px;
    }

    ul ul::before {
        content: '';
        border-left: var(--indentguide);
        position: absolute;
        left: calc(var(--line-height) / 2 - var(--indentguide-size));
        top: 0.2rem;
        bottom: 0.2rem;
    }

    ul ul:hover::before {
        border-left: var(--indentguide-active);
    }

    mark {
        background-color: var(--highlight-color);
    }
`;

// src/JsonViewer.ts
var JsonViewer = class extends LitElement {
  constructor() {
    super();
    this.state = {
      expanded: {},
      filtered: {},
      highlight: null
    };
    this.lastFocusedItem = null;
    this.#handlePropertyClick = (path) => (e) => {
      e.preventDefault();
      this.setState(toggleNode(path));
    };
    this.#handleFocusIn = (event) => {
      const target = event.target;
      if (event.target === this) {
        this.#focusItem(this.lastFocusedItem || this.nodeElements[0]);
      }
      if (target.matches('[role="treeitem"]')) {
        if (this.lastFocusedItem) {
          this.lastFocusedItem.tabIndex = -1;
        }
        this.lastFocusedItem = target;
        this.tabIndex = -1;
        target.tabIndex = 0;
      }
    };
    this.#handleFocusOut = (event) => {
      const relatedTarget = event.relatedTarget;
      if (!relatedTarget || !this.contains(relatedTarget)) {
        this.tabIndex = 0;
      }
    };
    this.addEventListener("focusin", this.#handleFocusIn);
    this.addEventListener("focusout", this.#handleFocusOut);
  }
  static {
    this.styles = [JsonViewer_styles_default];
  }
  static customRenderer(value, _path) {
    return JSON.stringify(value);
  }
  async setState(stateFn) {
    const currentState = this.state;
    this.state = {
      ...currentState,
      ...stateFn(currentState, this)
    };
  }
  connectedCallback() {
    if (!this.hasAttribute("data") && !isDefined(this.data)) {
      this.setAttribute("data", this.innerText);
    }
    this.setAttribute("role", "node");
    this.setAttribute("tabindex", "0");
    super.connectedCallback();
  }
  #handlePropertyClick;
  #handleFocusIn;
  #handleFocusOut;
  #handleKeyDown(event) {
    if (!["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) {
      return;
    }
    const nodes = [...this.nodeElements];
    const isLtr = this.matches(":dir(ltr)");
    const isRtl = this.matches(":dir(rtl)");
    if (nodes.length > 0) {
      event.preventDefault();
      const activeItemIndex = nodes.findIndex((item) => item.matches(":focus"));
      const activeItem = nodes[activeItemIndex];
      const isExpanded = this.state.expanded[activeItem.dataset.path];
      const isLeaf = isPrimitive(getValueByPath(this.data, activeItem.dataset.path));
      const focusItemAt = (index) => {
        const item = nodes[Math.max(Math.min(index, nodes.length - 1), 0)];
        this.#focusItem(item);
      };
      const toggleExpand = (expanded) => {
        this.setState(toggleNode(activeItem.dataset.path, expanded));
      };
      if (event.key === "ArrowDown") {
        focusItemAt(activeItemIndex + 1);
      } else if (event.key === "ArrowUp") {
        focusItemAt(activeItemIndex - 1);
      } else if (isLtr && event.key === "ArrowRight" || isRtl && event.key === "ArrowLeft") {
        if (!activeItem || isExpanded || isLeaf) {
          focusItemAt(activeItemIndex + 1);
        } else {
          toggleExpand(true);
        }
      } else if (isLtr && event.key === "ArrowLeft" || isRtl && event.key === "ArrowRight") {
        if (!activeItem || !isExpanded || isLeaf) {
          focusItemAt(activeItemIndex - 1);
        } else {
          toggleExpand(false);
        }
      } else if (event.key === "Home") {
        focusItemAt(0);
      } else if (event.key === "End") {
        focusItemAt(nodes.length - 1);
      }
    }
  }
  #focusItem(item) {
    item.focus();
  }
  expand(glob) {
    this.setState(expand(glob, true));
  }
  expandAll() {
    this.setState(expand("**", true));
  }
  collapseAll() {
    this.setState(expand("**", false));
  }
  collapse(glob) {
    this.setState(expand(glob, false));
  }
  *search(criteria) {
    for (const [node, path] of deepTraverse(this.data)) {
      if (isPrimitive(node) && String(node).match(criteria)) {
        this.expand(path);
        this.updateComplete.then(() => {
          const node2 = this.shadowRoot.querySelector(`[data-path="${path}"]`);
          node2.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "center"
          });
          node2.focus();
        });
        this.setState(highlight(path));
        yield {
          value: node,
          path
        };
      }
    }
    this.setState(highlight(null));
  }
  filter(criteria) {
    this.setState(filter(criteria));
  }
  resetFilter() {
    this.setState(resetFilter());
  }
  renderObject(node, path) {
    return html`
            <ul part="object" role="group">
                ${map(Object.entries(node), ([key, nodeData]) => {
      const nodePath = path ? `${path}.${key}` : key;
      const isPrimitiveNode = isPrimitive(nodeData);
      const isExpanded = this.state.expanded[nodePath];
      const isFiltered = this.state.filtered[nodePath];
      return isFiltered ? nothing : html`
                              <li
                                  part="property"
                                  role="treeitem"
                                  data-path="${nodePath}"
                                  aria-expanded="${isExpanded ? "true" : "false"}"
                                  tabindex="-1"
                                  .hidden="${this.state.filtered[nodePath]}"
                                  aria-hidden="${this.state.filtered[nodePath]}"
                              >
                                  <span
                                      part="key"
                                      class="${classMap({
        key,
        collapsable: !isPrimitiveNode,
        ["collapsable--collapsed"]: !this.state.expanded[nodePath]
      })}"
                                      @click="${!isPrimitiveNode ? this.#handlePropertyClick(nodePath) : null}"
                                  >
                                      ${key}:
                                      ${when(!isPrimitiveNode && !isExpanded, () => this.renderNodePreview(nodeData))}
                                  </span>

                                  ${when(isPrimitiveNode || isExpanded, () => this.renderValue(nodeData, nodePath))}
                              </li>
                          `;
    })}
            </ul>
        `;
  }
  renderValue(value, path = "") {
    if (isPrimitive(value)) {
      return this.renderPrimitive(value, path);
    }
    return this.renderObject(value, path);
  }
  renderNodePreview(node) {
    return html`<span part="preview" class="preview"> ${generateNodePreview(node)} </span>`;
  }
  renderPrimitive(node, path) {
    const highlight2 = this.state.highlight;
    const nodeType = getType(node);
    const renderedValue = this.constructor.customRenderer(node, path);
    const primitiveNode = html`
            <span part="primitive primitive-${nodeType}" class="${getType(node)}"> ${renderedValue} </span>
        `;
    return path === highlight2 ? html`<mark part="highlight">${primitiveNode}</mark>` : primitiveNode;
  }
  render() {
    const data = this.data;
    return html`
            <div
                part="base"
                @keydown=${this.#handleKeyDown}
                @focusin="${this.#handleFocusIn}"
                @focusout="${this.#handleFocusOut}"
            >
                ${when(isDefined(data), () => this.renderValue(data))}
            </div>
        `;
  }
};
__decorateClass([
  property({ converter: JSONConverter, type: Object })
], JsonViewer.prototype, "data", 2);
__decorateClass([
  state()
], JsonViewer.prototype, "state", 2);
__decorateClass([
  state()
], JsonViewer.prototype, "lastFocusedItem", 2);
__decorateClass([
  queryAll('[role="treeitem"]')
], JsonViewer.prototype, "nodeElements", 2);

export {
  JsonViewer
};
